import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetDescription, SheetTitle, cn } from '@mlm/ui';
import {
  getPosProductRecipe,
  type PosPriceMap,
  type PosProduct,
  type PosRecipeLine,
  type PosVariant,
} from '../../lib/api';
import { lineCap, lineKey, type CartLine } from '../../lib/cart-store';
import { productPrice, variantPrice } from '../../lib/pos-pricing';
import { PriceTag } from './price-tag';
import { ProductImage } from './product-image';
import { QtyStepper } from './qty-stepper';

/**
 * The item slide-over: pick a variation and a quantity, then Add. It opens for
 * a variation product (a single-SKU tile still adds on the tap itself) and
 * replaces the old "Choose a variation" dialog.
 */
export function ItemSheet({
  product,
  cart,
  priceMap,
  onAdd,
  onClose,
}: {
  /** The product being added; null closes the sheet. */
  product: PosProduct | null;
  /** To cap the quantity at what is left after the lines already in the cart. */
  cart: CartLine[];
  priceMap: PosPriceMap | null;
  onAdd: (p: PosProduct, v: PosVariant | null, qty: number) => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      open={!!product}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full max-w-full flex-col gap-0 rounded-l-[10px] p-0 data-[state=closed]:duration-300 data-[state=open]:duration-300 sm:w-[420px] sm:max-w-[420px]"
      >
        {product && (
          // Keyed so the picked variation and quantity never leak from the
          // previous product into the next one opened.
          <ItemSheetBody
            key={product.id}
            product={product}
            cart={cart}
            priceMap={priceMap}
            onAdd={onAdd}
            onClose={onClose}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

/** The recipe rows for one line: a variation's own rows REPLACE the product's. */
function recipeFor(lines: PosRecipeLine[], variantId: string | null): PosRecipeLine[] {
  if (variantId) {
    const own = lines.filter((l) => l.variantId === variantId);
    if (own.length > 0) return own;
  }
  return lines.filter((l) => !l.variantId);
}

function ItemSheetBody({
  product,
  cart,
  priceMap,
  onAdd,
  onClose,
}: {
  product: PosProduct;
  cart: CartLine[];
  priceMap: PosPriceMap | null;
  onAdd: (p: PosProduct, v: PosVariant | null, qty: number) => void;
  onClose: () => void;
}) {
  const hasVariants = product.variants.length > 0;
  // One sellable variation: pick it for the cashier. More: they choose.
  const sellable = product.variants.filter((v) => v.inStock);
  const [variant, setVariant] = useState<PosVariant | null>(
    sellable.length === 1 ? sellable[0]! : null,
  );
  const [qty, setQty] = useState(1);

  // What can still be added: the line's cap less what the cart already holds.
  const line = { product, variant };
  const inCart = cart.find((l) => lineKey(l) === lineKey(line))?.qty ?? 0;
  const room = hasVariants && !variant ? 0 : Math.max(0, lineCap(line) - inCart);
  const clamped = Math.min(qty, Math.max(room, 1));

  const unit = variant
    ? variantPrice(variant, priceMap)
    : hasVariants
      ? null
      : productPrice(product, priceMap);

  // What one unit uses up (cups, lids). Read-only: the sale consumes it.
  const recipeQ = useQuery({
    queryKey: ['pos-recipe', product.id],
    queryFn: () => getPosProductRecipe(product.id),
    staleTime: 5 * 60_000,
  });
  const recipe = recipeFor(recipeQ.data ?? [], variant?.id ?? null);

  const canAdd = room > 0 && (!hasVariants || !!variant);

  return (
    <>
      <div className="flex-1 space-y-5 overflow-y-auto p-[25px]">
        <ProductImage product={product} className="h-[180px] w-full rounded-[10px] text-4xl" />
        <div>
          <SheetTitle className="text-2xl font-semibold capitalize">{product.name}</SheetTitle>
          <SheetDescription className="font-mono text-xs">{product.sku}</SheetDescription>
        </div>

        {hasVariants && (
          <div className="space-y-2">
            <div className="text-lg font-semibold">Variation</div>
            <div className="grid grid-cols-2 gap-2">
              {product.variants.map((v) => {
                const selected = variant?.id === v.id;
                const price = variantPrice(v, priceMap);
                return (
                  <button
                    key={v.id}
                    type="button"
                    disabled={!v.inStock}
                    aria-pressed={selected}
                    onClick={() => {
                      setVariant(v);
                      setQty(1);
                    }}
                    className={cn(
                      'flex min-h-[50px] flex-col items-start justify-center rounded-[5px] px-3 py-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      selected ? 'bg-primary text-primary-foreground' : 'bg-muted',
                    )}
                  >
                    <span className="text-base font-medium leading-tight">{v.name}</span>
                    <span className={cn('text-xs', !selected && 'text-muted-foreground')}>
                      {!v.inStock
                        ? 'Out of stock'
                        : product.isVirtual
                          ? // Made to order: there is no stock figure to show.
                            `₱${price.now.toFixed(2)}`
                          : v.offlineStock != null
                            ? `₱${price.now.toFixed(2)} · last count ${Math.max(v.offlineStock, 0)}`
                            : `₱${price.now.toFixed(2)} · ${v.stockQty} in stock`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold">Quantity</div>
          <QtyStepper
            size="lg"
            qty={clamped}
            cap={Math.max(room, 1)}
            minusDisabled={clamped <= 1}
            onStep={(d) => setQty(Math.min(Math.max(clamped + d, 1), Math.max(room, 1)))}
            onSet={(n) => setQty(Math.min(Math.max(n, 1), Math.max(room, 1)))}
          />
        </div>
        {inCart > 0 && (
          <p className="-mt-3 text-right text-sm text-muted-foreground">
            {room > 0 ? `${inCart} already in the cart` : 'All available stock is in the cart'}
          </p>
        )}

        {recipe.length > 0 && (
          <div className="space-y-1">
            <div className="text-lg font-semibold">Uses</div>
            <ul className="text-base text-muted-foreground">
              {recipe.map((r) => (
                <li key={r.materialProductId} className="flex justify-between gap-3">
                  <span className="capitalize">{r.name}</span>
                  <span className="shrink-0 tabular-nums">
                    {r.qtyPerUnit * clamped}
                    {r.unitLabel ? ` ${r.unitLabel}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-4 border-t p-[25px]">
        <div className="flex items-baseline justify-between">
          <span className="text-lg font-semibold">Total</span>
          {unit ? (
            <span className="flex flex-col items-end">
              <span className="text-2xl font-semibold text-primary">
                ₱{(unit.now * clamped).toFixed(2)}
              </span>
              {unit.now < unit.base && (
                <span className="text-sm">
                  <PriceTag base={unit.base} now={unit.now} /> each
                </span>
              )}
            </span>
          ) : (
            <span className="text-base text-muted-foreground">Choose a variation</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-[50px] rounded-[10px] border-2 border-foreground text-base font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canAdd}
            onClick={() => {
              onAdd(product, variant, clamped);
              onClose();
            }}
            className="h-[50px] rounded-[10px] bg-foreground text-base font-semibold text-background disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>
    </>
  );
}
